import { LucideIcon } from 'lucide-react';
import { ReactNode } from 'react';

type BadgeVariant = 'success' | 'warning' | 'error' | 'info' | 'neutral' | 'primary';
type BadgeSize = 'sm' | 'md' | 'lg';

interface BadgeProps {
  variant?: BadgeVariant;
  size?: BadgeSize;
  icon?: LucideIcon;
  children: ReactNode;
  dot?: boolean;
  className?: string;
}

const variantClasses: Record<BadgeVariant, string> = {
  success: 'bg-green-100 text-green-800 border-green-200',
  warning: 'bg-yellow-100 text-yellow-800 border-yellow-200',
  error: 'bg-red-100 text-red-800 border-red-200',
  info: 'bg-blue-100 text-blue-800 border-blue-200',
  neutral: 'bg-gray-100 text-gray-800 border-gray-200',
  primary: 'bg-primary-100 text-primary-800 border-primary-200',
};

const dotClasses: Record<BadgeVariant, string> = {
  success: 'bg-green-500',
  warning: 'bg-yellow-500',
  error: 'bg-red-500',
  info: 'bg-blue-500',
  neutral: 'bg-gray-500',
  primary: 'bg-primary-500',
};

const sizeClasses: Record<BadgeSize, string> = {
  sm: 'px-2 py-0.5 text-xs',
  md: 'px-2.5 py-1 text-sm',
  lg: 'px-3 py-1.5 text-base',
};

export function Badge({
  variant = 'neutral',
  size = 'md',
  icon: Icon,
  children,
  dot = false,
  className = '',
}: BadgeProps) {
  return (
    <span
      className={`inline-flex items-center space-x-1 font-medium border rounded-full ${variantClasses[variant]} ${sizeClasses[size]} ${className}`}
    >
      {dot && (
        <span className={`w-1.5 h-1.5 rounded-full ${dotClasses[variant]}`} />
      )}
      {Icon && <Icon className="w-3.5 h-3.5" />}
      <span>{children}</span>
    </span>
  );
}

// Specific status badges for common use cases
export function StatusBadge({ status }: { status: string }) {
  const statusMap: Record<string, { variant: BadgeVariant; label: string }> = {
    active: { variant: 'success', label: 'Active' },
    inactive: { variant: 'error', label: 'Inactive' },
    pending: { variant: 'warning', label: 'Pending' },
    processing: { variant: 'info', label: 'Processing' },
    completed: { variant: 'success', label: 'Completed' },
    failed: { variant: 'error', label: 'Failed' },
    approved: { variant: 'success', label: 'Approved' },
    rejected: { variant: 'error', label: 'Rejected' },
    draft: { variant: 'neutral', label: 'Draft' },
    under_review: { variant: 'warning', label: 'Under Review' },
    deprecated: { variant: 'neutral', label: 'Deprecated' },
    legacy: { variant: 'warning', label: 'Legacy' },
    upcoming: { variant: 'info', label: 'Upcoming' },
  };

  const config = statusMap[status.toLowerCase()] || {
    variant: 'neutral' as BadgeVariant,
    label: status,
  };

  return (
    <Badge variant={config.variant} dot>
      {config.label}
    </Badge>
  );
}

// Progress badge
export function ProgressBadge({ current, total }: { current: number; total: number }) {
  const percentage = (current / total) * 100;
  let variant: BadgeVariant = 'neutral';

  if (percentage >= 100) variant = 'success';
  else if (percentage >= 75) variant = 'info';
  else if (percentage >= 50) variant = 'warning';
  else variant = 'error';

  return (
    <Badge variant={variant}>
      {current} / {total}
    </Badge>
  );
}

// Quality score badge
export function QualityBadge({ score }: { score: number }) {
  let variant: BadgeVariant = 'neutral';

  if (score >= 9) variant = 'success';
  else if (score >= 7.5) variant = 'info';
  else if (score >= 6) variant = 'warning';
  else variant = 'error';

  // Display whole numbers without decimal (8 instead of 8.0)
  const displayScore = score % 1 === 0 ? score.toFixed(0) : score.toFixed(1);

  return <Badge variant={variant}>{displayScore}/10</Badge>;
}
